// @flow
import React, { Fragment, useState } from 'react';
import { isEmpty } from 'lodash';
import classnames from 'classnames';

import { BlockLink } from 'Components/frameworks/block-link/BlockLink.jsx';
import { Button } from 'Components/frameworks/button/Button.jsx';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { Modal } from 'Components/frameworks/modal/Modal.jsx';
import { Price } from 'Components/frameworks/price/Price.jsx';
import { Spinner } from 'Components/frameworks/spinner/Spinner.jsx';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { MIN_HEIGHT } from 'Components/widgets/price-ticket/PriceTicket';
import { type PriceTicketContentProps } from 'Components/widgets/price-ticket/PriceTicketContentWrapper';
import { t } from 'Src/i18n';

export function PriceTicketNarrow(props: { ...PriceTicketContentProps, isLite: boolean }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleDetail = () => {
    setIsExpanded(!isExpanded);
  };

  const {
    className,
    button,
    hasCallCenter,
    info,
    isLite,
    isLoading,
    items = [],
    price,
    staticPrice,
    travelAdvisorConfig,
  } = props;

  const hasDetail = () => {
    return !isEmpty(items) || !isEmpty(info);
  };

  const classes = classnames(
    className,
    'bg-white border-b-1 border-t-1 border-gray-light',
    { 'opacity-75': isLoading },
    { fadeInLong: !isLite },
    { slideInBottom: isLite },
  );

  const hasPrice = !hasCallCenter && !isLite && !isEmpty(price);
  const hasBlockLink = hasCallCenter && !isLite;

  return (
    <div className={classes} style={{ minHeight: MIN_HEIGHT }} data-testid="PriceTicketNarrow">
      <SiteContainer>
        <div className="flex flex-col items-center py-5">
          {isLoading ? (
            <Spinner size="small" color={COLORS.BLUE} />
          ) : (
            <Fragment>
              {hasBlockLink &&
                travelAdvisorConfig && (
                  <BlockLink
                    texts={travelAdvisorConfig.text}
                    url={travelAdvisorConfig.href}
                    className="mb-3 text-gray-darker"
                    icon={ICONS.PHONE_BUBBLE}
                  />
                )}
              {hasPrice && (
                <button
                  className="flex reset-button"
                  data-testid="PriceTicketNarrow-toggler"
                  onClick={toggleDetail}
                  title={t(
                    `productPriceTicketLine.collapseLabels.${isExpanded ? 'collapse' : 'expand'}`,
                  )}
                  type="button"
                >
                  <Price
                    {...price}
                    from={t('price.format.from')}
                    instead={t('price.format.instead')}
                    className="text-gray-darker"
                    hasInitialPrice={false}
                    theme="ticket"
                  />
                  {hasDetail() && (
                    <Icon
                      className="rtl:ml-2"
                      color={COLORS.GRAY_LIGHT}
                      name={ICONS.QUESTION}
                      width="1.5rem"
                    />
                  )}
                </button>
              )}
              {staticPrice && (
                <TemplateString
                  className="block px-9 pb-5 text-center text-gray-darker text-base font-sans leading-one"
                  valuesClassName={{ amount: 'text-2xl underline font-brand leading-tight' }}
                  template={staticPrice.price}
                  values={{ amount: staticPrice.amount }}
                />
              )}
              <div className="w-full" style={{ maxWidth: '20rem' }}>
                <Button {...button} className="w-full" size="large" />
              </div>
            </Fragment>
          )}
        </div>
      </SiteContainer>
      {!hasCallCenter &&
        isExpanded &&
        hasDetail() && (
          <Modal
            ariaLabel={info && info.label}
            className="p-4"
            closeLabel={t('common.closeLabel')}
            onClose={toggleDetail}
          >
            <ul className="list-none p-0">
              {items.map((item, idx) => {
                return (
                  <li
                    className="border-b-1 border-gray-light flex items-center py-4"
                    key={idx}
                    data-testid="PriceTicketNarrow-item"
                  >
                    <Icon color={COLORS.BLUE} name={item.iconName} width="1.5rem" />
                    <div className="flex-1 font-sans leading-tight rtl:ml-3 text-base text-gray-darker">
                      <div>{item.label}</div>
                      <div className="font-bold text-blue">{item.description}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
            {!isEmpty(info) && (
              <div className="flex items-center mb-4">
                <Icon color={COLORS.BLUE} name={info.iconName} width="1.5rem" />
                <div
                  className="font-sans text-blue text-xl rtl:ml-3"
                  data-testid="PriceTicketNarrow-info"
                >
                  {info.label}
                </div>
              </div>
            )}
          </Modal>
        )}
    </div>
  );
}
